import PostCard from './home-components/PostCard';
import { SITELINK } from '../../app-routes/Links';
import TopBar from '../../layouts/~includes/TopBar';
import { fetchMyFeeds } from '../../api-services/feeds/useFetchMyPosts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getUser } from '../../app-utils';
import { UserInterface } from '../../app-schemas/profile.schema';
import { useEffect, useMemo, useState } from 'react';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import { useInView } from 'react-intersection-observer';
import { Loader } from '../../assets/custom-icons';
import { useTheme } from '../../context/theme-context';
import { SkeletonFeed } from '../../components/skeletons';

// main-component-ui
const MyPosts = () => {
  document.body.classList.remove('overflow-hidden');

  const [user, setUser] = useState<UserInterface | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const { ref, inView } = useInView();
  const { isDarkMode } = useTheme();

  // Load user data safely
  useEffect(() => {
    const loadUser = () => {
      const userData = getUser();
      setUser(userData);
      setIsLoadingUser(false);
    };
    loadUser();
  }, []);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['all feeds', user?.email],
    queryFn: fetchMyFeeds,
    initialPageParam: 1,
    enabled: !!user, // Only run query if user exists
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const content = useMemo(() =>
      data?.pages.flatMap((page) =>
        page.data.map((post: PostInterface, index: number) => (
          <PostCard
            key={post.post_id}
            index={post.post_id}
            post={post}
            innerRef={page.data.length - 2 === index + 1 ? ref : undefined}
          />
        )),
      ),
    [data, ref],
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  // Show loading state while checking user
  if (isLoadingUser) {
    return (
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-900' : 'bg-[#E1F0F8F7]'}`}>
        <div className="fixed top-0 w-full z-50">
          <TopBar title='Posts' link={SITELINK.HOME}/>
        </div>
        <div className="mt-[50px] mb-20">
          <SkeletonFeed count={3} variant="post" />
        </div>
      </section>
    );
  }

  // Handle case where user is not logged in
  if (!user) {
    return (
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-900' : 'bg-[#E1F0F8F7]'}`}>
        <div className="fixed top-0 w-full z-50">
          <TopBar title='Posts' link={SITELINK.HOME}/>
        </div>
        <div className="flex items-center justify-center h-[calc(100vh-50px)] mt-[50px]">
          <div className="text-center">
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Please log in to view your posts
            </p>
            <a href={SITELINK.LOGIN} className="text-blue-600 hover:underline">
              Go to Login
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-900' : 'bg-[#E1F0F8F7]'}`}>
      {/* top lead area and post btn */}
      <div className="fixed top-0 w-full z-50">
        <TopBar title='Posts' link={SITELINK.HOME}/>
      </div>

      {/* community feeds section */}
      <div className="mt-[50px] mb-20">
        {isLoading ? (
          <SkeletonFeed count={3} variant="post" />
        ) : (
          <>
            <div className="text-sm">{content}</div>
            {isFetchingNextPage && (
              <div className="flex py-5 justify-center">
                <img 
                  src={Loader} 
                  alt="loader" 
                  className="h-[20px] w-[20px] animate-spin"
                  style={{
                    filter: isDarkMode ? 'invert(1)' : 'none'
                  }}
                />
              </div>
            )}
            {!hasNextPage && data?.pages[0]?.data.length > 0 && (
              <div className={`text-xs flex justify-center py-5 ${
                isDarkMode ? 'bg-neutral-900 text-gray-400' : 'bg-white text-gray-600'
              }`}>
                No more content
              </div>
            )}
            {!hasNextPage && data?.pages[0]?.data.length === 0 && (
              <div className={`flex flex-col items-center justify-center h-[60vh] gap-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <div className="text-center">
                  <p className="text-lg mb-2">No posts yet</p>
                  <p className="text-sm">Create your first post to see it here!</p>
                  <a 
                    href={SITELINK.COMPOSER}
                    className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Post
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MyPosts;